/**
 * CommunicationSection — Customer Profile block (Module 8 Phase B, Section 12)
 *
 * Displays the current SMS + Email state, effective dates, and exposes the
 * "View Communication History" modal + authorized "Record Consent /
 * Withdrawal" action. Read-only for viewing; the manual record action opens
 * a separate dialog and always appends a new event (immutable history).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  MessageSquare, Mail, Clock, ShieldCheck, ShieldAlert, ShieldQuestion,
  History, PenSquare, Loader2, User, Globe, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Distinguish Unknown / affirmative / withdrawn via TEXT + ICON, not colour
// alone (Section 23 — accessibility).
const STATE_META = {
  unknown:        { label: 'Unknown / No Recorded Consent', icon: ShieldQuestion, tone: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  opted_in:       { label: 'Opted In',                      icon: ShieldCheck,    tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  opted_out:      { label: 'Opted Out',                     icon: ShieldAlert,    tone: 'bg-red-50 text-red-800 border-red-200' },
  subscribed:     { label: 'Subscribed',                    icon: ShieldCheck,    tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  unsubscribed:   { label: 'Unsubscribed',                  icon: ShieldAlert,    tone: 'bg-red-50 text-red-800 border-red-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

const StateBadge = ({ status }) => {
  const meta = STATE_META[status] || STATE_META.unknown;
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`${meta.tone} inline-flex items-center gap-1.5 font-medium`}
      data-testid={`state-badge-${status}`}>
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </Badge>
  );
};

const CommunicationSection = ({ userId, customerEmail, preferredContact, headers }) => {
  // person_id resolver — prefer user_id (post-conversion) then email (pre-conversion)
  const handle = userId || customerEmail;
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordDefaults, setRecordDefaults] = useState({ channel: 'sms', action: 'consent' });

  const fetchAll = useCallback(async () => {
    if (!handle) return;
    setLoading(true);
    try {
      const encoded = encodeURIComponent(handle);
      const [stateRes, audRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/communication/${encoded}/state`, { headers }),
        axios.get(`${API_URL}/api/admin/communication/${encoded}/audiences`, { headers })
          .catch(() => ({ data: { memberships: [] } })),
      ]);
      setState(stateRes.data);
      setMemberships(audRes.data?.memberships || []);
    } catch (err) {
      // 404 is fine — treat as Unknown
      setState({
        person_id: null,
        sms: { current_status: 'unknown', effective_at: null },
        email: { current_status: 'unknown', effective_at: null },
      });
      setMemberships([]);
    } finally { setLoading(false); }
  }, [handle, headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openRecord = (channel, action) => {
    setRecordDefaults({ channel, action });
    setRecordOpen(true);
  };

  if (!handle) return null;

  return (
    <Card className="border-0 shadow-sm" data-testid="communication-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-[#F56F05]" />
            Communication
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate(`/admin/communication?customer=${encodeURIComponent(handle)}`)} data-testid="open-conversation-btn">
              <MessageSquare className="h-3.5 w-3.5 mr-1" />
              Open Communication
            </Button>
            <Button size="sm" variant="outline" onClick={() => setHistoryOpen(true)} data-testid="view-history-btn">
              <History className="h-3.5 w-3.5 mr-1" />
              View Communication History
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="py-6 text-center text-zinc-400 text-sm"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading&hellip;</div>
        ) : (
          <>
            {/* Preferred Contact — visually + semantically separate from consent (Section 12) */}
            <div className="flex items-baseline gap-3 pb-3 border-b border-zinc-100">
              <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold w-32 flex-shrink-0">Preferred Contact</p>
              <p className="text-sm text-zinc-800 capitalize" data-testid="preferred-contact-value">{preferredContact || '\u2014'}</p>
              <p className="text-[10px] text-zinc-400 italic ml-auto">Preference only \u2014 does not imply consent.</p>
            </div>

            {/* SMS row */}
            <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr_auto] gap-2 sm:gap-4 items-center" data-testid="sms-row">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-zinc-500" />
                <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">SMS</p>
              </div>
              <div>
                <StateBadge status={state?.sms?.current_status || 'unknown'} />
                {state?.sms?.effective_at && (
                  <p className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1" data-testid="sms-effective">
                    <Clock className="h-3 w-3" /> Updated {fmtDate(state.sms.effective_at)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1.5 justify-end">
                <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-700 hover:bg-emerald-50"
                  onClick={() => openRecord('sms', 'consent')} data-testid="record-sms-consent-btn">
                  <PenSquare className="h-3 w-3 mr-1" /> Record Consent
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-700 hover:bg-red-50"
                  onClick={() => openRecord('sms', 'withdrawal')} data-testid="record-sms-withdrawal-btn">
                  <PenSquare className="h-3 w-3 mr-1" /> Record Opt-Out
                </Button>
              </div>
            </div>

            {/* Email row */}
            <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr_auto] gap-2 sm:gap-4 items-center" data-testid="email-row">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-zinc-500" />
                <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">Email</p>
              </div>
              <div>
                <StateBadge status={state?.email?.current_status || 'unknown'} />
                {state?.email?.effective_at && (
                  <p className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1" data-testid="email-effective">
                    <Clock className="h-3 w-3" /> Updated {fmtDate(state.email.effective_at)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1.5 justify-end">
                <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-700 hover:bg-emerald-50"
                  onClick={() => openRecord('email', 'consent')} data-testid="record-email-consent-btn">
                  <PenSquare className="h-3 w-3 mr-1" /> Record Consent
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-700 hover:bg-red-50"
                  onClick={() => openRecord('email', 'withdrawal')} data-testid="record-email-unsubscribe-btn">
                  <PenSquare className="h-3 w-3 mr-1" /> Record Unsubscribe
                </Button>
              </div>
            </div>

            {/* Audience Memberships — read-only, auto-synced (Section 18) */}
            <AudienceMemberships memberships={memberships} handle={handle} headers={headers} onReevaluated={fetchAll} />
          </>
        )}
      </CardContent>

      <CommunicationHistoryModal
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        handle={handle}
        headers={headers}
      />
      <RecordEventDialog
        open={recordOpen}
        onOpenChange={setRecordOpen}
        handle={handle}
        headers={headers}
        defaults={recordDefaults}
        onRecorded={() => { setRecordOpen(false); fetchAll(); }}
      />
    </Card>
  );
};

// =============================================================================
// History modal (newest-first, read-only)
// =============================================================================
const EVENT_LABELS = {
  website_sms_opt_in:         'SMS Opt-In (Website)',
  website_email_subscribe:    'Email Subscribe (Website)',
  sms_opt_out:                'SMS Opt-Out',
  email_unsubscribe:          'Email Unsubscribe',
  sms_opt_back_in:            'SMS Opt-Back-In',
  email_resubscribe:          'Email Resubscribe',
  admin_recorded_consent:     'Admin-Recorded Consent',
  admin_recorded_withdrawal:  'Admin-Recorded Withdrawal',
  provider_callback:          'Provider Callback',
  migration_init_unknown:     'Initialised as Unknown',
  reconcile_correction:       'Reconciliation Correction',
};

const CommunicationHistoryModal = ({ open, onOpenChange, handle, headers }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [canSeeEvidence, setCanSeeEvidence] = useState(false);

  useEffect(() => {
    if (!open || !handle) return;
    setLoading(true);
    axios.get(`${API_URL}/api/admin/communication/${encodeURIComponent(handle)}/history`, { headers })
      .then(r => { setRows(r.data?.events || []); setCanSeeEvidence(!!r.data?.viewer_can_see_evidence); })
      .catch(() => { setRows([]); setCanSeeEvidence(false); })
      .finally(() => setLoading(false));
  }, [open, handle, headers]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="communication-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-[#F56F05]" /> Communication History
          </DialogTitle>
          <DialogDescription>
            Every consent, withdrawal, and status change ever recorded for this person. Newest first &mdash; read-only.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-2 -mr-2">
          {loading ? (
            <div className="py-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading&hellip;</div>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-zinc-500 italic">No communication events on file.</p>
          ) : (
            <ol className="relative border-l-2 border-zinc-100 ml-2 space-y-4" data-testid="history-timeline">
              {rows.map((ev) => (
                <li key={ev.id} className="pl-5 relative" data-testid={`history-event-${ev.id}`}>
                  <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-[#F56F05] border-2 border-white shadow" />
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-900">
                        {EVENT_LABELS[ev.event_type] || ev.event_type}
                        <span className="text-[10px] uppercase text-zinc-400 ml-2">{ev.channel}</span>
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        <StateBadge status={ev.resulting_state} />
                      </p>
                    </div>
                    <p className="text-[11px] text-zinc-500 flex-shrink-0 whitespace-nowrap">{fmtDateTime(ev.effective_at)}</p>
                  </div>
                  <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-zinc-600">
                    {ev.source && (<><dt className="text-zinc-400">Source</dt><dd className="truncate">{ev.source}</dd></>)}
                    {ev.actor_type && (<><dt className="text-zinc-400">Actor</dt><dd className="capitalize flex items-center gap-1"><User className="h-3 w-3" />{ev.actor_type}</dd></>)}
                    {ev.source_page && (<><dt className="text-zinc-400">Page</dt><dd className="truncate flex items-center gap-1"><Globe className="h-3 w-3" />{ev.source_page}</dd></>)}
                    {ev.consent_language_version && (<><dt className="text-zinc-400">Language Version</dt><dd className="font-mono text-[10px]">{ev.consent_language_version}</dd></>)}
                    {ev.reason && (<><dt className="text-zinc-400">Notes</dt><dd className="whitespace-pre-wrap col-span-full">{ev.reason}</dd></>)}
                    {canSeeEvidence && ev.ip_address && (<><dt className="text-zinc-400">IP</dt><dd className="font-mono text-[10px]">{ev.ip_address}</dd></>)}
                    {canSeeEvidence && ev.user_agent && (<><dt className="text-zinc-400">User-Agent</dt><dd className="font-mono text-[10px] truncate" title={ev.user_agent}>{ev.user_agent}</dd></>)}
                  </dl>
                  {ev.consent_language_snapshot && canSeeEvidence && (
                    <details className="mt-1.5 text-[11px]">
                      <summary className="cursor-pointer text-zinc-500 hover:text-[#F56F05]">Exact language shown</summary>
                      <blockquote className="mt-1 pl-3 border-l-2 border-zinc-200 text-zinc-600 italic">{ev.consent_language_snapshot}</blockquote>
                    </details>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="pt-3 border-t border-zinc-100 text-[10px] text-zinc-400 flex items-center justify-between">
          <span>{rows.length} event{rows.length === 1 ? '' : 's'}</span>
          {!canSeeEvidence && <span className="flex items-center gap-1"><ArrowRight className="h-3 w-3" />Sensitive evidence hidden for your role</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Record consent / withdrawal dialog
// =============================================================================
const SOURCE_PRESETS = [
  'Phone call',
  'In person',
  'Signed form',
  'Email correspondence',
  'Text conversation',
  'Other (describe in notes)',
];

const RecordEventDialog = ({ open, onOpenChange, handle, headers, defaults, onRecorded }) => {
  const [channel, setChannel] = useState('sms');
  const [action, setAction] = useState('consent');
  const [sourcePreset, setSourcePreset] = useState(SOURCE_PRESETS[0]);
  const [sourceCustom, setSourceCustom] = useState('');
  const [reason, setReason] = useState('');
  const [effective, setEffective] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChannel(defaults?.channel || 'sms');
    setAction(defaults?.action || 'consent');
    setSourcePreset(SOURCE_PRESETS[0]);
    setSourceCustom('');
    setReason('');
    // Default effective to now (local ISO for datetime-local input)
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    setEffective(d.toISOString().slice(0, 16));
  }, [open, defaults]);

  const submit = async () => {
    const source = sourcePreset === SOURCE_PRESETS[SOURCE_PRESETS.length - 1]
      ? sourceCustom.trim()
      : `${sourcePreset}${sourceCustom.trim() ? ` \u2014 ${sourceCustom.trim()}` : ''}`;
    if (!source || source.length < 2) {
      toast.error('Source is required \u2014 describe how the consent was received.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        channel, action, source,
        reason: reason.trim() || undefined,
        effective_at: effective ? new Date(effective).toISOString() : undefined,
      };
      const r = await axios.post(
        `${API_URL}/api/admin/communication/${encodeURIComponent(handle)}/record`,
        payload, { headers },
      );
      toast.success(`${channel.toUpperCase()} ${action} recorded`);
      if (r.data?.event?.duplicate) {
        toast.info('This exact action was already recorded in this minute \u2014 no duplicate event created.');
      }
      onRecorded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record event');
    } finally { setSaving(false); }
  };

  const isConsent = action === 'consent';
  const channelLabel = channel === 'sms' ? 'SMS' : 'Email';
  const dialogTitle = `Record ${channelLabel} ${isConsent ? 'Consent' : 'Withdrawal'}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="record-event-dialog">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isConsent ? 'text-emerald-700' : 'text-red-700'}`}>
            {isConsent ? <ShieldCheck className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
            {dialogTitle}
          </DialogTitle>
          <DialogDescription>
            {isConsent
              ? 'Document consent you received off-website (phone, in person, signed form). This appends a new event \u2014 previous events are never modified.'
              : 'Document a withdrawal you received off-website. Appends a new event \u2014 previous events are never modified.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="record-channel-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Action</Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger data-testid="record-action-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="consent">Consent</SelectItem>
                  <SelectItem value="withdrawal">Withdrawal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">How was this received? <span className="text-red-500">*</span></Label>
            <Select value={sourcePreset} onValueChange={setSourcePreset}>
              <SelectTrigger data-testid="record-source-preset-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCE_PRESETS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              value={sourceCustom}
              onChange={e => setSourceCustom(e.target.value)}
              placeholder={sourcePreset === SOURCE_PRESETS[SOURCE_PRESETS.length - 1] ? 'Describe the source' : 'Optional detail (e.g. date/time or form name)'}
              className="mt-1.5 text-sm"
              data-testid="record-source-detail"
            />
            <p className="text-[10px] text-zinc-400 italic mt-1">
              &ldquo;Existing customer&rdquo; alone is not sufficient proof \u2014 describe how the consent was received.
            </p>
          </div>

          <div>
            <Label className="text-xs">Effective at</Label>
            <Input type="datetime-local" value={effective} onChange={e => setEffective(e.target.value)}
              data-testid="record-effective-at" />
          </div>

          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value.slice(0, 1000))}
              placeholder="Any supporting detail (call log ID, signed-form location, etc.)"
              className="min-h-[60px] resize-none text-sm"
              data-testid="record-notes" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="record-cancel-btn">Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className={isConsent
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-red-600 hover:bg-red-700 text-white'}
            data-testid="record-submit-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Record {isConsent ? 'Consent' : 'Withdrawal'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Audience Memberships (read-only, auto-synced from canonical CRM data)
// =============================================================================
const CATEGORY_LABELS = {
  communication:      'Communication Status',
  frequency:          'Frequency',
  customer_type:      'Customer Type',
  property_type:      'Property Type',
  service:            'Service',
  lead_source:        'Lead Source',
  preferred_contact:  'Preferred Contact',
};

const AudienceMemberships = ({ memberships, handle, headers, onReevaluated }) => {
  const [refreshing, setRefreshing] = useState(false);
  const grouped = React.useMemo(() => {
    const byCat = {};
    (memberships || []).forEach(m => {
      (byCat[m.category] = byCat[m.category] || []).push(m);
    });
    return byCat;
  }, [memberships]);
  const orderedCats = ['communication', 'frequency', 'customer_type', 'property_type', 'service', 'lead_source', 'preferred_contact'];

  const reevaluate = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API_URL}/api/admin/communication/${encodeURIComponent(handle)}/audiences/reevaluate`,
        {}, { headers });
      toast.success('Memberships re-synced');
      onReevaluated?.();
    } catch {
      toast.error('Re-sync failed');
    } finally { setRefreshing(false); }
  };

  return (
    <div className="pt-3 border-t border-zinc-100" data-testid="audience-memberships">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">Audience Memberships</p>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-400 italic">Auto-synced. Read-only.</span>
          <Button size="sm" variant="ghost" className="h-6 text-[11px] px-1.5 text-zinc-500 hover:text-[#F56F05]"
            onClick={reevaluate} disabled={refreshing} data-testid="reevaluate-audiences-btn">
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Re-sync now'}
          </Button>
        </div>
      </div>
      {(!memberships || memberships.length === 0) ? (
        <p className="text-xs text-zinc-400 italic py-2" data-testid="audience-empty">
          No audience memberships yet &mdash; will populate automatically as canonical fields change.
        </p>
      ) : (
        <div className="space-y-2">
          {orderedCats.map(cat => {
            const rows = grouped[cat];
            if (!rows || rows.length === 0) return null;
            return (
              <div key={cat} data-testid={`audience-cat-${cat}`}>
                <p className="text-[10px] text-zinc-500 mb-1">{CATEGORY_LABELS[cat] || cat}</p>
                <div className="flex flex-wrap gap-1.5">
                  {rows.map(m => (
                    <Badge key={m.audience_id} variant="outline"
                      className="bg-orange-50 text-[#C2410C] border-orange-200 text-[11px] font-medium"
                      data-testid={`audience-chip-${m.rule_key}`}>
                      {m.label}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


export default CommunicationSection;
